import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import PhotoUpload from "@/components/common/PhotoUpload";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash, Warning } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Raw Material (Oli Bekas)", "Supporting Material", "Lainnya"];
const RETUR_MATERIALS = ["Oli Bekas", "Minyak Bakar", "Presol"];
const RETUR_DENSITY_DEFAULT = { "Oli Bekas": 0.87, "Minyak Bakar": 0.92, "Presol": 0.87 };
const RETUR_SOURCES = [
  { v: "customer", l: "Dari Customer (retur DO)" },
  { v: "internal", l: "Dari Internal (produksi/loading)" },
];

/** Universal weigh form. direction = 'in' | 'out' */
export default function WeighPage({ direction = "in" }) {
  const { user } = useAuth();
  const canReset = ["admin", "owner"].includes(user?.role);
  const [txnType, setTxnType] = useState("receiving");
  const [mode, setMode] = useState("new");  // new | existing | retur (for direction='in' receiving)
  const [queue, setQueue] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [deliveries, setDeliveries] = useState([]); // untuk datalist No. DO asal (retur)
  const [sel, setSel] = useState(null);
  const [weight, setWeight] = useState("");
  const [operator, setOperator] = useState("");
  const [photoTruck, setPhotoTruck] = useState("");
  const [photoDisplay, setPhotoDisplay] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  // New-receiving fields (only used when direction=in & txn=receiving & mode=new)
  const [newRcv, setNewRcv] = useState({
    supplier: "", material: "Oli Bekas", category: "Raw Material (Oli Bekas)",
    truck_no: "", driver: "", destination_tank: "", po_no: "",
  });
  const setNR = (k) => (v) => setNewRcv((n) => ({ ...n, [k]: v?.target ? v.target.value : v }));

  // Retur-specific state (mode=retur, direction=in, txn=receiving)
  const [returForm, setReturForm] = useState({
    supplier: "", material: "Oli Bekas", retur_source: "customer",
    retur_do_no: "", retur_density: 0.87,
    destination_tank: "", truck_no: "", driver: "",
  });
  const setRet = (k) => (v) => setReturForm((r) => ({ ...r, [k]: v?.target ? v.target.value : v }));
  const setReturMaterial = (m) => setReturForm((r) => ({
    ...r, material: m, retur_density: RETUR_DENSITY_DEFAULT[m] || 0.87,
  }));

  // ---- Reset / Batalkan Slip (admin & owner only) ----
  const [resetTarget, setResetTarget] = useState(null); // slip row
  const [resetReason, setResetReason] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);

  const doReset = async () => {
    if (!resetTarget) return;
    if (resetReason.trim().length < 5) {
      toast.error("Alasan wajib diisi minimal 5 karakter untuk audit");
      return;
    }
    setResetSubmitting(true);
    try {
      const { data } = await api.delete(`/weigh/${resetTarget.id}`, { data: { reason: resetReason.trim() } });
      const rev = data.rollback?.tank_delta_liter;
      toast.success(
        `Slip ${data.slip_no} dihapus. Status dokumen: ${data.rollback?.reverted_status || "-"}` +
        (rev ? ` · Stok tangki dikembalikan ${Math.abs(rev).toLocaleString("id-ID")} L` : "")
      );
      setResetTarget(null);
      setResetReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setResetSubmitting(false); }
  };

  const load = () => {
    api.get("/weigh/queue", { params: { direction } }).then((r) => setQueue(r.data)).catch(() => {});
    api.get("/weigh").then((r) => setRows(r.data)).catch(() => {});
    api.get("/master/tanks").then((r) => setTanks(r.data)).catch(() => {});
    if (direction === "in") {
      api.get("/delivery?status=closed").then((r) => setDeliveries(r.data || [])).catch(() => {});
    }
  };
  useAutoRefresh(load, { deps: [direction] });

  const filtered = queue.filter((q) => q._kind === txnType);
  const useCreateNew = direction === "in" && txnType === "receiving" && mode === "new";
  const useCreateRetur = direction === "in" && txnType === "receiving" && mode === "retur";

  const submit = async (e) => {
    e.preventDefault();
    if (!weight) return toast.error("Isi berat gross");
    if (!useCreateNew && !useCreateRetur && !sel) return toast.error("Pilih transaksi terlebih dahulu");
    if (useCreateNew && !newRcv.supplier) return toast.error("Isi nama supplier terlebih dahulu");
    if (useCreateRetur) {
      if (!returForm.supplier) return toast.error("Isi nama Customer / Sumber Internal");
      if (!returForm.destination_tank) return toast.error("Tank tujuan wajib diisi untuk barang retur");
    }
    if (!photoTruck) return toast.error("Foto Truck di Jembatan Timbang wajib diisi");
    if (!photoDisplay) return toast.error("Foto Display Timbang wajib diisi");
    setSubmitting(true);
    try {
      let ref = sel;
      if (useCreateNew) {
        // 1) Create receiving reference first (regular)
        const { data } = await api.post("/receiving", { ...newRcv, estimated_qty: parseFloat(weight) || 0 });
        ref = data;
        toast.success(`Referensi Receiving dibuat: ${data.ref_no}`);
      } else if (useCreateRetur) {
        // 1) Create RETUR receiving reference
        const { data } = await api.post("/receiving", {
          supplier: returForm.supplier,
          material: returForm.material,
          category: "Retur",
          truck_no: returForm.truck_no,
          driver: returForm.driver,
          destination_tank: returForm.destination_tank,
          po_no: "",
          estimated_qty: 0,
          notes: "",
          is_retur: true,
          retur_source: returForm.retur_source,
          retur_do_no: returForm.retur_do_no,
          retur_density: parseFloat(returForm.retur_density) || 0,
        });
        ref = data;
        toast.success(`Referensi RETUR dibuat: ${data.ref_no} — lanjut Timbang Keluar di Penerimaan`);
      }
      await api.post("/weigh", {
        txn_type: txnType,
        ref_id: ref.id,
        ref_no: ref.ref_no || ref.do_no,
        truck_no: ref.truck_no || newRcv.truck_no || returForm.truck_no || "-",
        driver: ref.driver || newRcv.driver || returForm.driver || "",
        weight: parseFloat(weight),
        operator,
        photo_truck: photoTruck,
        photo_display: photoDisplay,
        direction,
      });
      toast.success(`Slip timbang ${direction === "in" ? "masuk" : "keluar"} tersimpan`);
      setSel(null); setWeight(""); setOperator(""); setPhotoTruck(""); setPhotoDisplay("");
      setNewRcv({ supplier: "", material: "Oli Bekas", category: "Raw Material (Oli Bekas)", truck_no: "", driver: "", destination_tank: "", po_no: "" });
      setReturForm({ supplier: "", material: "Oli Bekas", retur_source: "customer", retur_do_no: "", retur_density: 0.87, destination_tank: "", truck_no: "", driver: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const title = direction === "in" ? "Timbang Masuk" : "Timbang Keluar";
  const subtitle = direction === "in"
    ? "Truk baru datang → timbang di sini (bisa langsung buat Referensi Receiving reguler atau Barang Retur)"
    : "Setelah bongkar / loading selesai → catat gross truk kosong";

  return (
    <AppShell title={title} subtitle={subtitle}>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <form onSubmit={submit} data-testid={`weigh-${direction}-form`}
              className="xl:col-span-3 rounded-xl bg-white border border-slate-200 p-4 sm:p-6 shadow-sm space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Jenis Transaksi">
              <Select value={txnType} onValueChange={(v) => { setTxnType(v); setSel(null); setMode(v === "receiving" ? "new" : "existing"); }}>
                <SelectTrigger data-testid={`weigh-${direction}-type`} className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="receiving">Receiving (Supplier)</SelectItem>
                  <SelectItem value="delivery">Delivery (Kiriman Keluar)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={`Gross ${direction === "in" ? "In" : "Out"} (kg)`} required>
              <Input data-testid={`weigh-${direction}-weight`} type="number" step="0.01"
                     value={weight} onChange={(e) => setWeight(e.target.value)} className="h-11 font-mono text-lg" required />
            </Field>
          </div>

          {/* Mode switch: only for direction='in' & receiving */}
          {direction === "in" && txnType === "receiving" && (
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="w-full grid grid-cols-3 h-11 mb-2">
                <TabsTrigger value="new" data-testid="weigh-mode-new"
                  className="data-[state=active]:bg-sky-600 data-[state=active]:text-white gap-2 text-[12px] sm:text-sm">
                  🆕 Referensi Baru
                </TabsTrigger>
                <TabsTrigger value="retur" data-testid="weigh-mode-retur"
                  className="data-[state=active]:bg-orange-500 data-[state=active]:text-white gap-2 text-[12px] sm:text-sm">
                  🔄 Barang Retur
                </TabsTrigger>
                <TabsTrigger value="existing" data-testid="weigh-mode-existing"
                  className="data-[state=active]:bg-slate-900 data-[state=active]:text-white gap-2 text-[12px] sm:text-sm">
                  📋 Antrian ({filtered.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="new" className="mt-3 space-y-3">
                <p className="text-[11px] text-slate-500">
                  Truk supplier baru datang tanpa registrasi? Isi info dasar di sini — referensi Receiving akan dibuat otomatis begitu Anda klik simpan.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Supplier" required><Input data-testid="new-rcv-supplier" value={newRcv.supplier} onChange={setNR("supplier")} required placeholder="CV Oli Sejahtera" /></Field>
                  <Field label="Nama Barang"><Input data-testid="new-rcv-material" value={newRcv.material} onChange={setNR("material")} /></Field>
                  <Field label="Nomor Truck"><Input data-testid="new-rcv-truck" value={newRcv.truck_no} onChange={setNR("truck_no")} className="font-mono" placeholder="B 1234 XYZ" /></Field>
                  <Field label="Driver"><Input data-testid="new-rcv-driver" value={newRcv.driver} onChange={setNR("driver")} /></Field>
                  <Field label="PO / SJ"><Input data-testid="new-rcv-po" value={newRcv.po_no} onChange={setNR("po_no")} /></Field>
                  <Field label="Tank Tujuan">
                    <Select value={newRcv.destination_tank} onValueChange={setNR("destination_tank")}>
                      <SelectTrigger data-testid="new-rcv-tank" className="h-10"><SelectValue placeholder="Pilih tank" /></SelectTrigger>
                      <SelectContent>{tanks.filter(t=>t.type!=="production_area").map((t) => <SelectItem key={t.id} value={t.code}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
              </TabsContent>

              <TabsContent value="retur" className="mt-3 space-y-3">
                <div className="rounded-md bg-orange-50 border border-orange-200 p-3 text-[12px] text-orange-900">
                  <b>Barang Retur:</b> Barang yang dikembalikan (dari Customer atau Internal). Alur: Timbang Masuk di sini → Timbang Keluar di halaman <b>Penerimaan</b> → stok otomatis nambah ke tangki tujuan. Skip Lab & SPB.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Sumber Retur" required>
                    <Select value={returForm.retur_source} onValueChange={setRet("retur_source")}>
                      <SelectTrigger data-testid="retur-source" className="h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>{RETUR_SOURCES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Nama Customer / Sumber Internal" required>
                    <Input data-testid="retur-supplier" value={returForm.supplier} onChange={setRet("supplier")}
                      required placeholder={returForm.retur_source === "internal" ? "misal: Divisi Produksi" : "misal: PT ABC Sejahtera"} />
                  </Field>
                  <Field label="Barang Retur" required>
                    <Select value={returForm.material} onValueChange={setReturMaterial}>
                      <SelectTrigger data-testid="retur-material" className="h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>{RETUR_MATERIALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label={`Density (kg/L) — default ${RETUR_DENSITY_DEFAULT[returForm.material] || 0.87}`}>
                    <Input data-testid="retur-density" type="number" step="0.001"
                      value={returForm.retur_density} onChange={setRet("retur_density")} className="font-mono" />
                  </Field>
                  <Field label="No. DO Asal (opsional)">
                    <Input data-testid="retur-do" list="retur-do-list" value={returForm.retur_do_no}
                      onChange={setRet("retur_do_no")} placeholder="Ketik / pilih DO yang selesai" className="font-mono" />
                    <datalist id="retur-do-list">
                      {deliveries.map((d) => (
                        <option key={d.id} value={d.do_no}>{`${d.do_no} · ${d.customer || ''} · ${d.product || ''}`}</option>
                      ))}
                    </datalist>
                  </Field>
                  <Field label="Tank Tujuan" required>
                    <Select value={returForm.destination_tank} onValueChange={setRet("destination_tank")}>
                      <SelectTrigger data-testid="retur-tank" className="h-10"><SelectValue placeholder="Pilih tank" /></SelectTrigger>
                      <SelectContent>
                        {tanks
                          .filter((t) => t.type !== "production_area")
                          .map((t) => {
                            const match = (t.product || "").toLowerCase() === (returForm.material || "").toLowerCase();
                            return (
                              <SelectItem key={t.id} value={t.code}>
                                {match ? "★ " : ""}{t.name}{t.product ? ` · ${t.product}` : ""}
                              </SelectItem>
                            );
                          })}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Nomor Truck"><Input data-testid="retur-truck" value={returForm.truck_no} onChange={setRet("truck_no")} className="font-mono" placeholder="B 1234 XYZ" /></Field>
                  <Field label="Driver"><Input data-testid="retur-driver" value={returForm.driver} onChange={setRet("driver")} /></Field>
                </div>
              </TabsContent>

              <TabsContent value="existing" className="mt-3">
                <Field label="Pilih Referensi Receiving" required={mode === "existing"}>
                  <Select value={sel?.id || ""} onValueChange={(id) => setSel(filtered.find((q) => q.id === id) || null)}>
                    <SelectTrigger data-testid="weigh-in-ref-existing" className="h-10">
                      <SelectValue placeholder={filtered.length ? "Pilih transaksi…" : "Belum ada referensi dibuat"} />
                    </SelectTrigger>
                    <SelectContent>
                      {filtered.map((q) => (
                        <SelectItem key={q.id} value={q.id}>
                          {q.ref_no || q.do_no} · {q.supplier || q.customer} {q.is_retur ? "· RETUR" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </TabsContent>
            </Tabs>
          )}

          {/* For delivery or weigh-out: keep old flow */}
          {!(direction === "in" && txnType === "receiving") && (
            <Field label={txnType === "receiving" ? "Referensi Receiving" : "Referensi Delivery Order"} required>
              <Select value={sel?.id || ""} onValueChange={(id) => setSel(filtered.find((q) => q.id === id) || null)}>
                <SelectTrigger data-testid={`weigh-${direction}-ref`} className="h-10">
                  <SelectValue placeholder={filtered.length ? "Pilih transaksi…" : "Tidak ada antrian"} />
                </SelectTrigger>
                <SelectContent>
                  {filtered.map((q) => (
                    <SelectItem key={q.id} value={q.id}>
                      {(q.ref_no || q.do_no)} · {q.supplier || q.customer}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          <Field label="Operator Timbang" required>
            <Input data-testid={`weigh-${direction}-operator`} value={operator} onChange={(e) => setOperator(e.target.value)} required />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PhotoUpload label="Foto Truck di Jembatan Timbang" value={photoTruck} onChange={setPhotoTruck} testid={`weigh-${direction}-photo-truck`} required />
            <PhotoUpload label="Foto Display Timbang" value={photoDisplay} onChange={setPhotoDisplay} testid={`weigh-${direction}-photo-display`} required />
          </div>

          <div className="flex justify-end">
            <Button type="submit" data-testid={`weigh-${direction}-submit`} disabled={submitting}
                    className={`${useCreateRetur ? "bg-orange-600 hover:bg-orange-700" : "bg-slate-900 hover:bg-slate-800"} text-white h-11 px-6`}>
              {submitting ? "Menyimpan…" : useCreateRetur
                ? "Simpan Retur + Timbang Masuk"
                : `Simpan Timbang ${direction === "in" ? "Masuk" : "Keluar"}`}
            </Button>
          </div>
        </form>

        {/* Right: recent slips */}
        <div className="xl:col-span-2 rounded-xl bg-white border border-slate-200 p-5 shadow-sm">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800 mb-3">Slip Terbaru</h3>
          <div className="space-y-2 max-h-[560px] overflow-y-auto">
            {rows.filter((r) => r.direction === direction).slice(0, 15).map((r) => (
              <div key={r.id} className="p-3 rounded-lg border border-slate-100 relative group" data-testid={`slip-card-${r.slip_no}`}>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[12px] font-bold text-slate-800">{r.slip_no}</div>
                  <div className="flex items-center gap-2">
                    <div className="font-mono text-[13px] font-bold text-sky-700">{r.weight?.toLocaleString("id-ID")} kg</div>
                    {canReset && (
                      <button
                        onClick={() => { setResetTarget(r); setResetReason(""); }}
                        data-testid={`slip-reset-${r.slip_no}`}
                        title="Reset / batalkan slip ini (admin only)"
                        className="p-1.5 rounded hover:bg-red-50 text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash size={14} weight="duotone" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{r.ref_no} · {r.truck_no}</div>
                <div className="text-[10px] text-slate-400 font-mono mt-0.5">{new Date(r.created_at).toLocaleString("id-ID")}</div>
              </div>
            ))}
            {rows.filter((r) => r.direction === direction).length === 0 && (
              <div className="py-8 text-center text-xs text-slate-400">Belum ada slip.</div>
            )}
          </div>
        </div>
      </div>

      {/* Dialog Konfirmasi Reset Slip */}
      <Dialog
        open={!!resetTarget}
        onOpenChange={(o) => { if (!o && !resetSubmitting) { setResetTarget(null); setResetReason(""); } }}
      >
        <DialogContent className="max-w-lg" data-testid="slip-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Warning size={22} weight="fill" className="text-red-600" />
              Reset Slip Timbang
            </DialogTitle>
            <DialogDescription className="text-[13px] pt-2">
              Aksi ini akan <b>menghapus slip permanen</b> & mengembalikan status dokumen ke <b>sebelum ditimbang</b>.
              Jika sudah closed & stok tangki bertambah (retur/lab), stok akan <b>dikurangi kembali otomatis</b>.
              Seluruh aksi tercatat di <span className="font-mono">edit_logs</span>.
            </DialogDescription>
          </DialogHeader>
          {resetTarget && (
            <div className="space-y-4 mt-2">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[12px]">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Slip</div>
                    <div className="font-mono font-bold text-slate-800">{resetTarget.slip_no}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Berat</div>
                    <div className="font-mono font-bold text-sky-700">{resetTarget.weight?.toLocaleString("id-ID")} kg</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Referensi</div>
                    <div className="font-mono text-slate-700">{resetTarget.ref_no}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Kendaraan</div>
                    <div className="font-mono text-slate-700">{resetTarget.truck_no || "-"}</div>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600">
                  Alasan Reset <span className="text-red-500">*</span>
                </label>
                <Textarea
                  data-testid="slip-reset-reason"
                  value={resetReason}
                  onChange={(e) => setResetReason(e.target.value)}
                  placeholder="misal: Security salah pencet, mobil belum timbang keluar sebenarnya"
                  rows={3}
                  className="mt-1.5"
                  disabled={resetSubmitting}
                />
                <div className="text-[10px] text-slate-400 mt-1">Minimal 5 karakter — akan dicatat di audit log.</div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setResetTarget(null); setResetReason(""); }} disabled={resetSubmitting}>
                  Batal
                </Button>
                <Button
                  type="button"
                  onClick={doReset}
                  disabled={resetSubmitting || resetReason.trim().length < 5}
                  data-testid="slip-reset-confirm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {resetSubmitting ? "Memproses…" : "Ya, Reset Slip"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
